import { forwardRef, useState, useEffect, useRef, useCallback } from 'react';

import { debounce } from 'lodash';
import { RiShieldFill } from 'react-icons/ri';

import {
  Alert,
  Box,
  Divider,
  Group,
  Slider,
  SliderProps,
  Stack,
  Text,
  TextInput
} from '@ui';
import { convertToINR } from '@utils';

import classes from './CustomSlider.module.css';

interface CustomSliderProps extends SliderProps {
  title?: string;
  subTitle?: string;
  max?: number;
  min?: number;
  value?: number;
  step?: number;
  type?: 'money' | 'age';
  skipFrom?: number;
  disabled?: boolean;
  refresh?: boolean;
  hideTextInput?: boolean;
  disableTextInput?: boolean;
  customMinError?: string;
  customMaxError?: string;
  onChange?: (value: number) => void;
  onLocalValChange?: (value: number) => void;
  recommendedRange?: { min: number; max: number };
}

const CustomSlider = forwardRef<HTMLDivElement, CustomSliderProps>(
  (
    {
      title,
      subTitle,
      max = 100,
      min = 0,
      value,
      step = 1,
      type = 'money',
      skipFrom,
      disabled,
      refresh,
      hideTextInput,
      disableTextInput,
      customMinError,
      customMaxError,
      recommendedRange,
      onChange,
      onLocalValChange,
      ...rest
    },
    ref
  ) => {
    const [localValue, setLocalValue] = useState<number>(value ? value : min);
    const isTextFocussed = useRef<boolean>(false);
    const [error, setError] = useState<string>('');

    useEffect(() => {
      if (refresh) {
        setLocalValue(value ? value : min);
      }
    }, [refresh, value, min]);

    useEffect(() => {
      setLocalValue(value ? value : min);
    }, [value, min]);

    const debouncedOnChange = useRef(
      debounce((currValue: number) => {
        onChange?.(currValue);
      }, 500)
    );

    const handleFocus = () => {
      isTextFocussed.current = true;
    };

    const sanitizeValue = (input: any) => {
      if (!input || input === '') {
        return 0;
      } else if (typeof input === 'number') {
        return input;
      } else if (typeof input === 'string') {
        return parseInt(input.replace(/,/g, ''));
      } else {
        return input;
      }
    };

    const handleBlur = (input: any) => {
      let finalVal = min;
      const val = sanitizeValue(input);
      if (val <= min) {
        finalVal = min;
      } else if (val >= max) {
        finalVal = max;
      } else {
        finalVal = val;
      }

      if (value !== localValue) {
        setLocalValue(finalVal);
        debouncedOnChange.current(finalVal);
      }
      isTextFocussed.current = false;
    };

    const handleChange = useCallback(
      (newValue: number) => {
        let finalVal = min;
        if (type === 'age' && skipFrom) {
          if (newValue > skipFrom) {
            finalVal = max;
          } else {
            finalVal = newValue;
          }
        } else {
          finalVal = newValue;
        }

        setLocalValue(finalVal);

        if (
          type === 'age' &&
          finalVal >= min &&
          finalVal <= max &&
          newValue !== localValue
        ) {
          debouncedOnChange.current(finalVal);
        } else if (!isTextFocussed.current && newValue !== localValue) {
          debouncedOnChange.current(finalVal);
        }

        onLocalValChange?.(finalVal);
        if (error) {
          setError('');
        }
      },
      [
        min,
        max,
        localValue,
        customMaxError,
        customMinError,
        type,
        skipFrom,
        onLocalValChange,
        error
      ]
    );

    const handleTextInputChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = event.target.value;
        const numValue = sanitizeValue(inputValue);
        handleChange(numValue);
      },
      [handleChange]
    );

    // whenever max,min value changes recheck age is within min and max
    // trigger custom errors if props are set
    useEffect(() => {
      if (
        ((max && value && value > max) || (min && value && value < min)) &&
        type === 'age'
      ) {
        setError(value && value < min ? customMinError ?? '' : customMaxError ?? '');
        handleChange(value && value > max ? max : min);
      } else {
        setError('');
      }
    }, [max, min, value, type, customMinError, customMaxError, handleChange]);

    const valueLabelFormat = (value: number) => {
      return type === 'age' ? value : convertToINR(value, 2);
    };

    return (
      <Stack w="100%" ref={ref} className={classes.root}>
        <Group justify="space-between" wrap="nowrap">
          <Stack gap="xs">
            {title && (
              <Text fz={18} fw={600}>
                {title}
              </Text>
            )}
            {subTitle && (
              <Text fz={13} c="secondary">
                {subTitle}
              </Text>
            )}
          </Stack>
          {!hideTextInput && (
            <TextInput
              size="xl"
              value={localValue.toString()}
              onChange={handleTextInputChange}
              onFocus={handleFocus}
              onBlur={(e) => handleBlur(e.target.value)}
              disabled={disabled || disableTextInput}
              classNames={{ input: classes.textInput }}
              placeholder=""
              maw={140}
            />
          )}
          {hideTextInput && (
            <Box className={classes.coverLabel}>
              <Text fz={14} fw={600}>
                {type === 'money' ? convertToINR(localValue, 2) : localValue}
              </Text>
            </Box>
          )}
        </Group>

        <Box pos="relative">
          <Slider
            value={localValue}
            min={min}
            max={max}
            step={step}
            onChange={handleChange}
            disabled={disabled}
            color="primary"
            thumbSize={20}
            label={valueLabelFormat}
            marks={[
              { value: min, label: type === 'age' ? min : convertToINR(min, 2) },
              { value: max, label: type === 'age' ? max : convertToINR(max, 2) }
            ]}
            classNames={{
              thumb: classes.thumb,
              bar: classes.bar,
              track: classes.track,
              markLabel: classes.markLabel,
              mark: classes.mark
            }}
            thumbChildren={<RiShieldFill size={12} />}
            {...rest}
          />
          {recommendedRange &&
            recommendedRange.min !== undefined &&
            recommendedRange.max !== undefined &&
            recommendedRange.min >= min &&
            recommendedRange.max <= max && (
              <>
                <Box
                  className={classes.recommendedRange}
                  style={
                    {
                      '--rec-min': recommendedRange.min,
                      '--rec-max': recommendedRange.max,
                      '--min': min,
                      '--max': max
                    } as React.CSSProperties
                  }
                >
                  <Text fz={10} c="primary.4" fw={600}>
                    Recommended Till
                  </Text>
                </Box>
                <Divider
                  className={classes.recommendedRangeBar}
                  style={
                    {
                      '--rec-min': recommendedRange.min,
                      '--rec-max': recommendedRange.max,
                      '--min': min,
                      '--max': max
                    } as React.CSSProperties
                  }
                />
              </>
            )}
        </Box>

        {error && (
          <Alert color="orange" className={classes.errorAlert}>
            {error}
          </Alert>
        )}
      </Stack>
    );
  }
);

CustomSlider.displayName = 'CustomSlider';

export { CustomSlider };
export type { CustomSliderProps };
